import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useContent } from "@/context/ContentContext";
import { useText } from "@/lib/useText";
import { api } from "@/lib/api";
import { FeaturedPhoto } from "./FeaturedPhoto";

function MarqueeRow({ items, reverse, offset, onOpen, paused, setPaused, className = "" }) {
  const ordered = [...items.slice(offset), ...items.slice(0, offset)];
  const loop = [...ordered, ...ordered];
  const duration = `${Math.max(30, ordered.length * 9)}s`;
  return (
    <div className={`group/marquee relative overflow-hidden ${className}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid={reverse ? "gallery-marquee-2" : "gallery-marquee-1"}>
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-eden-bg to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-eden-bg to-transparent" aria-hidden="true" />
      <ul className={`flex w-max gap-3 ${reverse ? "animate-marquee-reverse" : "animate-marquee"} motion-reduce:animate-none`} style={{ "--marquee-duration": duration, animationPlayState: paused ? "paused" : "running" }}>
        {loop.map((it, i) => {
          const realIndex = items.indexOf(it);
          const dup = i >= ordered.length;
          return (
            <li key={`${it.id}-${i}`} aria-hidden={dup || undefined}>
              <button type="button" tabIndex={dup ? -1 : 0} onClick={() => onOpen(realIndex)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} className="group relative block h-44 w-72 overflow-hidden rounded-md border border-eden-border bg-eden-surface text-left transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-eden-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-eden-orange sm:h-52 sm:w-80" aria-label={it.caption ? `Ampliar: ${it.caption}` : `Ampliar imagem ${realIndex + 1}`} data-testid={dup ? undefined : `gallery-item-${realIndex}`}>
                <img src={it.url} alt={it.caption || `Screenshot ${realIndex + 1} do EDEN RP`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                {it.caption && <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-eden-bg/90 to-transparent px-3 pb-3 pt-8 text-xs font-medium text-eden-text">{it.caption}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Gallery() {
  const { content } = useContent();
  const t = useText();
  const fromDiscord = content.media.gallery_source === "discord";
  const [remote, setRemote] = useState([]);
  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    if (!fromDiscord) return undefined;
    let alive = true;
    const load = () => api.get("/discord/media").then(({ data }) => { if (alive) { setRemote(data.items || []); setFeatured(data.featured || null); } }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [fromDiscord]);
  const items = fromDiscord ? remote : (content.media.gallery || []);
  const [index, setIndex] = useState(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length]);

  if (!items.length) return null;
  const current = index === null ? null : items[index];

  return (
    <section data-reveal className="border-t border-eden-border py-20 sm:py-28" aria-labelledby="gallery-title" data-testid="gallery-section">
      <div className="container-eden">
        <div className="mb-12 grid gap-4 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">{t("gallery_eyebrow")}</p>
            <h2 id="gallery-title" className="text-2xl font-bold tracking-tight text-eden-text sm:text-3xl lg:text-4xl" data-testid="gallery-title">{content.media.gallery_title}</h2>
          </div>
          {content.media.gallery_text && <p className="text-eden-secondary lg:col-span-6 lg:col-start-7 lg:self-end" data-testid="gallery-text">{content.media.gallery_text}</p>}
        </div>
      </div>
      {featured && <FeaturedPhoto photo={featured} onOpen={() => setIndex(Math.max(0, items.findIndex((i) => i.id === featured.id)))} t={t} />}
      <MarqueeRow items={items} reverse={false} offset={0} onOpen={setIndex} paused={paused} setPaused={setPaused} />
      {items.length > 3 && <MarqueeRow items={items} reverse offset={Math.ceil(items.length / 2)} onOpen={setIndex} paused={paused} setPaused={setPaused} className="mt-3" />}
      <p className="container-eden mt-4 text-xs text-eden-muted">Passa o rato para pausar · clica numa imagem para ampliar</p>

      <Dialog open={current !== null} onOpenChange={(o) => !o && setIndex(null)}>
        <DialogContent className="max-w-6xl border-eden-border !bg-[#0b0b0d] p-2 sm:p-3 [&>button]:hidden" data-testid="gallery-lightbox">
          {current && (
            <div className="relative">
              <DialogTitle className="sr-only">{current.caption || "Imagem ampliada"}</DialogTitle>
              <img src={current.url} alt={current.caption || "Screenshot do EDEN RP"} className="max-h-[80vh] w-full object-contain" data-testid="gallery-lightbox-image" />
              <div className="flex items-center justify-between gap-4 px-2 pt-3 text-sm text-eden-secondary">
                <span data-testid="gallery-lightbox-caption">{current.caption}</span>
                <span className="shrink-0 font-mono text-xs text-eden-muted">{index + 1} / {items.length}</span>
              </div>
              <button type="button" onClick={() => setIndex(null)} className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full bg-eden-bg/80 text-eden-text hover:bg-eden-orange hover:text-eden-bg" aria-label="Fechar" data-testid="gallery-close"><X size={18} /></button>
              {items.length > 1 && (
                <>
                  <button type="button" onClick={() => setIndex((index - 1 + items.length) % items.length)} className="absolute left-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-eden-bg/80 text-eden-text hover:bg-eden-orange hover:text-eden-bg" aria-label="Anterior" data-testid="gallery-prev"><ChevronLeft size={20} /></button>
                  <button type="button" onClick={() => setIndex((index + 1) % items.length)} className="absolute right-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-eden-bg/80 text-eden-text hover:bg-eden-orange hover:text-eden-bg" aria-label="Seguinte" data-testid="gallery-next"><ChevronRight size={20} /></button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
